import React, { useMemo } from 'react';
import './MysticalBackground.css';

const Sparkle = React.memo(({ x, y, delay, size, color }: { 
  x: number; 
  y: number;
  delay: number;
  size: number;
  color: string;
}) => (
  <div 
    className="mystical-sparkle"
    style={{
      '--sparkle-x': x,
      '--sparkle-y': y,
      '--sparkle-size': size,
      '--sparkle-color': color,
      animationDelay: `${delay}s`
    } as React.CSSProperties}
  />
));

const FloatingOrb = React.memo(({ x, y, delay, size, color }: { 
  x: number; 
  y: number;
  delay: number;
  size: number;
  color: string;
}) => (
  <div 
    className="floating-orb"
    style={{
      '--orb-x': x,
      '--orb-y': y,
      '--orb-size': size,
      '--orb-color': color,
      animationDelay: `${delay}s`
    } as React.CSSProperties}
  />
));

const MysticalPlatform = React.memo(({ x, y, width, type }: { 
  x: number; 
  y: number;
  width: number;
  type: string;
}) => (
  <div 
    className={`mystical-platform platform-${type}`}
    style={{
      left: `${x}%`,
      bottom: `${y}%`,
      width: `${width}%`
    }}
  />
));

const MysticalBackground = React.memo(() => {
  const sparkles = useMemo(() => 
    Array.from({ length: 25 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 8,
      size: 0.5 + Math.random() * 1.5,
      color: ['#FF69B4', '#9370DB', '#00CED1', '#FFD700', '#FF6347'][Math.floor(Math.random() * 5)]
    })),
  []);

  const orbs = useMemo(() => 
    Array.from({ length: 8 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: 20 + Math.random() * 60,
      delay: Math.random() * 12,
      size: 0.8 + Math.random() * 1.2,
      color: ['#9370DB', '#FF69B4', '#00CED1', '#FFD700'][Math.floor(Math.random() * 4)]
    })),
  []);

  const platforms = useMemo(() => 
    Array.from({ length: 4 }).map((_, i) => ({
      id: i,
      x: 15 + i * 20 + Math.random() * 10,
      y: 20 + i * 8 + Math.random() * 15,
      width: 18 + Math.random() * 12,
      type: ['crystal', 'cloud', 'star'][Math.floor(Math.random() * 3)]
    })),
  []);

  return (
    <div className="mystical-background">
      {/* Magical sky */}
      <div className="mystical-sky" />
      
      {/* Rainbow aurora */}
      <div className="mystical-aurora">
        <div className="aurora-band band-1" />
        <div className="aurora-band band-2" />
        <div className="aurora-band band-3" />
      </div>
      
      {/* Magical mountains */}
      <div className="mystical-mountains">
        <div className="mountain mountain-1" />
        <div className="mountain mountain-2" />
        <div className="mountain mountain-3" />
      </div>
      
      {/* Mystical platforms */}
      <div className="platforms-layer">
        {platforms.map(platform => (
          <MysticalPlatform
            key={platform.id}
            x={platform.x}
            y={platform.y}
            width={platform.width}
            type={platform.type}
          />
        ))}
      </div>
      
      {/* Floating orbs */}
      <div className="orbs-layer">
        {orbs.map(orb => (
          <FloatingOrb
            key={orb.id}
            x={orb.x}
            y={orb.y}
            delay={orb.delay}
            size={orb.size}
            color={orb.color}
          />
        ))}
      </div>
      
      {/* Sparkles */}
      <div className="sparkles-layer">
        {sparkles.map(sparkle => (
          <Sparkle
            key={sparkle.id}
            x={sparkle.x}
            y={sparkle.y}
            delay={sparkle.delay}
            size={sparkle.size}
            color={sparkle.color}
          />
        ))}
      </div>
      
      {/* Mystical trees */}
      <div className="mystical-trees">
        <div className="mystical-tree tree-1" />
        <div className="mystical-tree tree-2" />
        <div className="mystical-tree tree-3" />
      </div>
      
      {/* Magical ground */}
      <div className="mystical-ground" />
      
      {/* Energy wisps */}
      <div className="energy-wisps">
        <div className="wisp wisp-1" />
        <div className="wisp wisp-2" />
        <div className="wisp wisp-3" />
        <div className="wisp wisp-4" />
      </div>
      
      {/* Magical particles */}
      <div className="mystical-particles" />
      
      {/* Portal effect */}
      <div className="mystical-portal">
        <div className="portal-ring ring-1" />
        <div className="portal-ring ring-2" />
        <div className="portal-ring ring-3" />
        <div className="portal-center" />
      </div>
    </div>
  );
});

export default MysticalBackground;